import { useEffect, useState } from "react";

import { IDataFetcherResponseError } from "@common/utils/use-query/types";
import { INSTANCE_RESTART_TIMEOUT } from "@stores/instances/constants";
import { IInstance } from "@stores/instances/types";
import { isInstanceUsable } from "@stores/instances/utils";
import { purchasesStore } from "@stores/purchases";
import { IPurchase } from "@stores/purchases/types";

export const useRefreshInstanceUntilReady = (
  id: IPurchase["id"],
  reloadInterval = 5000,
): {
  error?: IDataFetcherResponseError<any> | null;
  instance?: IInstance | null;
  isInstanceReady: boolean;
  isSelfHosted: boolean;
} => {
  const { error, data: purchase, refetch } = purchasesStore.useGetPurchase(id);
  const instance = purchase?.hosted_instance;

  // A self-hosted purchase has no instance to wait for, so it can never become
  // ready. Without this it would poll forever on the callers' behalf. Read off
  // the fetched purchase, not the caller's copy, so that a cloud -> self-hosted
  // change is picked up rather than leaving the caller waiting on an instance
  // that no longer exists.
  const isSelfHosted = !!purchase && !instance;

  // A healthy read is not trusted until the restart window has passed — see
  // INSTANCE_RESTART_TIMEOUT.
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    setHasSettled(false);

    const timeout = setTimeout(
      () => setHasSettled(true),
      INSTANCE_RESTART_TIMEOUT,
    );

    return () => clearTimeout(timeout);
  }, [id]);

  const isInstanceReady =
    hasSettled && !!instance && isInstanceUsable(instance);

  // Keep polling through the settle window too, so the reading we trust once
  // it closes is a fresh one
  useEffect(() => {
    if (isInstanceReady || isSelfHosted) {
      return;
    }

    const interval = setInterval(() => refetch(), reloadInterval);

    return () => clearInterval(interval);
  }, [isInstanceReady, isSelfHosted, refetch, reloadInterval]);

  return {
    error,
    instance,
    isInstanceReady,
    isSelfHosted,
  };
};
